import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'
import { createTheme, ThemeProvider } from '@mui/material'

export interface MuiThemeState {}

export interface MuiThemeAction {
  type: ''
  payload: any
}

const MuiTheme = createContext<{
  toggleColorMode: () => void
  colorMode: 'light' | 'dark'
  isDarkMode: boolean
} | null>(null)

const MuiThemeProvider: ReactFC = ({ children }) => {
  const [colorMode, setColorMode] = useState<'light' | 'dark'>('light')

  const toggleColorMode = useCallback(() => {
    setColorMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'))
  }, [])

  const isDarkMode = colorMode === 'dark'

  const theme = useMemo(
    () =>
      createTheme({
        breakpoints: {
          values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1280,
            xl: 1440
          }
        },
        zIndex: {
          modal: 999999999
        },
        typography: {
          fontFamily: ['Poppins'].join(',')
        },
        palette: {
          error: {
            main: '#FF0000',
            dark: '#e30000'
          },
          mode: colorMode,
          border: {
            main: '#C5C5C5'
          },
          primary: {
            main: '#007FFF',
            dark: '#0059B2',
            light: '#E2EDF8',
            ...(isDarkMode
              ? {
                  100: '#004C99',
                  200: '#79BBFC'
                }
              : {
                  100: '#C2E0FF',
                  200: '#79BBFC'
                })
          },
          warning: {
            main: '#FFD747',
            light: '#FFECAA',
            dark: '#EACB5E'
          },
          success: {
            main: '#28C76F'
          },
          light: {
            main: '#fff',
            100: '#F3F6F9'
          },
          background: {
            ...(isDarkMode
              ? {
                  default: '#001E3C',
                  paper: '#13304B'
                }
              : {
                  default: '#F4F5FA',
                  paper: '#FDFEFE'
                })
          },
          text: {
            ...(isDarkMode
              ? {
                  primary: '#f0f0f0',
                  primaryLight: '#d3d3d3'
                }
              : {
                  primary: '#373737',
                  primaryLight: '#4E4E4E'
                })
          }
        },
        components: {
          MuiSelect: {
            styleOverrides: {},
            defaultProps: {
              size: 'small'
            }
          },
          MuiTextField: {
            defaultProps: {
              size: 'small'
            }
          },
          MuiFormControl: {
            styleOverrides: {
              root: {
                '& .MuiFormHelperText-root': {
                  marginLeft: '0'
                }
              }
            },
            defaultProps: {
              size: 'small'
            }
          },
          MuiInputBase: {
            styleOverrides: {
              sizeSmall: {
                fontSize: '14px'
              }
            }
          },
          MuiInputLabel: {
            styleOverrides: {
              formControl: {
                ...(isDarkMode && {
                  color: '#fff'
                })
              },
              root: {
                fontSize: '14px',
                ...(isDarkMode && {
                  color: 'white',
                  '&.Mui-focused': {
                    color: 'white'
                  }
                })
              }
            }
          },
          MuiChip: {
            defaultProps: {
              size: 'small'
            }
          }
        }
        // ...(isDarkMode && {
        //   MuiOutlinedInput: {
        //     styleOverrides: {
        //       focused: {
        //         borderColor: 'white'
        //       },
        //       notchedOutline: {
        //         borderColor: 'white !important'
        //       }
        //     }
        //   }
        // })
      }),
    [colorMode]
  )

  return (
    <MuiTheme.Provider
      value={{
        toggleColorMode,
        colorMode,
        isDarkMode: colorMode === 'dark'
      }}
    >
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </MuiTheme.Provider>
  )
}
export default MuiThemeProvider

export const useMuiTheme = () => useContext(MuiTheme)!
